import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/ThemeProvider';
import { useToastContext } from '@/contexts/ToastContext';
import { useAppStore } from '@/store';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { DiagramEditor } from '@/components/DiagramEditor';
import {
  PlusCircle,
  Edit,
  Save,
  X,
  AlertCircle,
  ArrowLeft,
  FileDown,
  Sparkles,
  CheckCircle2,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useRef } from 'react';
import { getWeekDates, findTrainingYearForDate, TRAINING_START_BY_YEAR } from '@/utils/dateUtils';
import { weeklyReportService, mainJobService, exportService } from '@/api/services';
import { testApiConnection, testWeeklyReportEndpoint } from '@/utils/debug';
import { CreateDailyReportData } from '@/types';

interface DailyReport {
  id: number;
  date: string;
  description: string;
  hours_spent: number;
  day_name?: string;
}

interface MainJobOperation {
  id: number;
  step_number: number;
  operation_description: string;
  tools_used: string;
}

interface WeeklyReportData {
  id?: number;
  week_number: number;
  start_date: string;
  end_date: string;
  total_hours: number;
  daily_reports: DailyReport[];
  main_job: {
    id?: number;
    title: string;
    operations: MainJobOperation[];
  };
  is_ai_enhanced?: boolean;
  is_enhanced?: boolean;
  enhancement_date?: string;
  enhanced_at?: string;
  ai_enhanced?: boolean;
  enhancement_count?: number;
  enhanced_with_ai?: boolean;
  ai_enhancement_date?: string;
  original_content?: string;
  original_daily_reports?: DailyReport[];
  content?: string;
}

// Reverting an AI-enhanced report back to its original inputs costs tokens.
const REVERT_TOKEN_COST = 50;

export const WeeklyReportDetailPage: React.FC = () => {
  const params = useParams<{ weekNumber: string }>();
  const weekNumber = params?.weekNumber;
  const { fetchUserBalance, profile, userBalance } = useAppStore();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  
  // Debug profile data
  console.log('=== PROFILE DEBUG ===');
  console.log('Profile object:', profile);
  console.log('Profile program:', profile?.program);
  console.log('Profile type:', typeof profile?.program);
  console.log('====================');
  
  const [reportData, setReportData] = useState<WeeklyReportData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingDaily, setIsAddingDaily] = useState(false);
  const [isEditingMainJob, setIsEditingMainJob] = useState(false);
  const [isEditingMainJobTitle, setIsEditingMainJobTitle] = useState(false);
  const [isEditingDiagram, setIsEditingDiagram] = useState(false);
  const [dailyHours, setDailyHours] = useState<{[key: string]: number}>({});
  const [dailyDescriptions, setDailyDescriptions] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<'daily' | 'mainjob' | 'diagram'>('daily');
  const exportMenuRef = useRef<HTMLDivElement | null>(null);
  const hasLoadedData = useRef(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  
  // Local state for operations editing
  const [editingOperations, setEditingOperations] = useState<MainJobOperation[]>([]);

  const { register, handleSubmit, getValues, formState: { errors } } = useForm();

  const getTrainingYear = () => {
    return findTrainingYearForDate(new Date()) ?? Math.max(...Object.keys(TRAINING_START_BY_YEAR).map(Number));
  };

  // Function to determine college based on user's course
  const formatDate = (dateString: string): string => {
    // Parse date directly from string to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    return `${day}/${month}/${year}`;
  };

  const getCollegeName = (): string => {
    if (!profile?.program) {
      console.log('No program found in profile, defaulting to CoET');
      return 'College of Engineering and Technology (CoET)'; // Default fallback
    }

    const program = profile.program;
    console.log('Current program from profile:', program);
    
    // CoICT courses (using the enum values from types)
    const coictCourses = [
      'COMPUTER', // BSc in Computer Science
      'ELECTRONIC_SCIENCE', // BSc in Electronic Science and Communication
      'COMPUTER_ENGINEERING', // BSc in Computer Engineering and Information Technology
      'TELECOMMUNICATIONS', // BSc in Telecommunications Engineering
      'BUSINESS_IT' // BSc in Business Information Technology
    ];

    // SoMG courses (School of Mines and Geology)
    const somgCourses = [
      'MINING_ENGINEERING', // BSc in Mining Engineering
      'GEOLOGICAL_ENGINEERING', // BSc in Geological Engineering
      'MINERAL_PROCESSING', // BSc in Mineral Processing Engineering
      'PETROLEUM_ENGINEERING', // BSc in Petroleum Engineering
      'GEOLOGY', // BSc in Geology
      'GEOPHYSICS', // BSc in Geophysics
      'ENVIRONMENTAL_GEOLOGY' // BSc in Environmental Geology
    ];

    console.log('Checking against CoICT courses:', coictCourses);
    console.log('Checking against SoMG courses:', somgCourses);

    // Check if the user's program is in CoICT
    if (coictCourses.includes(program)) {
      console.log('Program matched CoICT, returning full CoICT name');
      return 'College of Information and Communication Technologies (CoICT)';
    }

    // Check if the user's program is in SoMG
    if (somgCourses.includes(program)) {
      console.log('Program matched SoMG, returning full SoMG name');
      return 'School of Mines and Geology (SoMG)';
    }

    console.log('Program not found in CoICT or SoMG, defaulting to full CoET name');
    // All other courses are CoET
    return 'College of Engineering and Technology (CoET)';
  };

  useEffect(() => {
    // Balance drives the export cost/affordability UI.
    fetchUserBalance();
  }, [fetchUserBalance]);

  useEffect(() => {
    // close export menu when clicking outside
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    const loadWeekData = async () => {
      if (!weekNumber) return;
      
      setIsLoading(true);
      
      try {
        const weekNum = parseInt(weekNumber);
        console.log('Loading week data for week:', weekNum);
        
        hasLoadedData.current = true;
        
        // Test API connection only once
        await testApiConnection();
        await testWeeklyReportEndpoint(weekNum);
        
        // Fetch weekly report from backend
        const weeklyReport = await weeklyReportService.getWeeklyReportByWeek(weekNum);
        console.log('Weekly report from backend:', weeklyReport);
        console.log('Main job from backend:', weeklyReport?.main_job);
        
        // Fetch daily reports for this week from backend
        const weekDailyReports = await weeklyReportService.getDailyReportsByWeek(weekNum);
        console.log('Daily reports from backend:', weekDailyReports);
        
        // Calculate total hours
        const totalHours = weekDailyReports.reduce((sum, report) => sum + report.hours_spent, 0);

        // Calculate week dates using utility function
        const weekDates = getWeekDates(weekNum, getTrainingYear());

        // Fetch main job operations if main job exists
        let mainJobOperations: MainJobOperation[] = [];
        if (weeklyReport?.main_job?.id) {
          try {
            console.log('Fetching operations for main job ID:', weeklyReport.main_job.id);
            mainJobOperations = await mainJobService.getOperations(weeklyReport.main_job.id);
            console.log('Main job operations from backend:', mainJobOperations);
          } catch (error) {
            console.warn('Failed to fetch main job operations:', error);
            console.error('Error details:', error);
          }
        } else {
          console.log('No main job ID found, skipping operations fetch');
        }

        setReportData({
          id: weeklyReport?.id,
          week_number: weekNum,
          start_date: weekDates.startDateStr,
          end_date: weekDates.endDateStr,
          total_hours: totalHours,
          is_ai_enhanced: weeklyReport?.is_ai_enhanced,
          daily_reports: weekDailyReports || [],
          main_job: {
            id: weeklyReport?.main_job?.id,
            title: weeklyReport?.main_job?.title || '',
            operations: mainJobOperations
          }
        });

        // Initialize dailyHours state with existing daily reports
        const initialDailyHours: { [key: string]: number } = {};
        const initialDailyDescriptions: { [key: string]: string } = {};
        
        // Map daily reports to days based on date
        weekDailyReports.forEach(report => {
          const reportDate = new Date(report.date);
          const dayName = reportDate.toLocaleDateString('en-US', { weekday: 'long' });
          if (dayName) {
            initialDailyHours[dayName] = report.hours_spent || 0;
            initialDailyDescriptions[dayName] = report.description || '';
          }
        });
        
        setDailyHours(initialDailyHours);
        setDailyDescriptions(initialDailyDescriptions);
      } catch (error) {
        console.error('Failed to load week data:', error);
        setReportData(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadWeekData();
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [weekNumber]); // Only depend on weekNumber

  // Calculate total hours automatically
  const calculateTotalHours = () => {
    let total = 0;
    
    // Calculate total from current form data (dailyHours state) and existing daily reports
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].forEach(dayName => {
      const dailyReport = getDailyReportForDay(dayName);
      let dayHours = 0;
      
      // Priority: dailyHours state (user input) > daily report from backend
      if (dailyHours[dayName] !== undefined) {
        dayHours = typeof dailyHours[dayName] === 'number' ? dailyHours[dayName] : parseFloat(dailyHours[dayName]) || 0;
      } else if (dailyReport?.hours_spent !== undefined) {
        dayHours = typeof dailyReport.hours_spent === 'number' ? dailyReport.hours_spent : parseFloat(dailyReport.hours_spent) || 0;
      }
      
      total += dayHours;
    });
    
    return total;
  };

  const handleSaveReport = async (data: any) => {
    try {
      console.log('Saving report:', data);
      
      if (!weekNumber) return;
      const weekNum = parseInt(weekNumber);
      const weekDates = getWeekDates(weekNum, getTrainingYear());
      
      // Prepare the weekly report data with daily reports
      const weeklyReportData = {
        week_number: weekNum,
        start_date: weekDates.startDateStr,
        end_date: weekDates.endDateStr,
        summary: data.summary || `Week ${weekNum} Report`,
        main_job_title: data.main_job_title || reportData?.main_job?.title || 'Main Job',
        main_job_description: data.main_job_description || 'Description of main job',
        // Include daily report data for each day
        daily_monday: data.daily_monday || '',
        hours_monday: data.hours_monday || 0,
        daily_tuesday: data.daily_tuesday || '',
        hours_tuesday: data.hours_tuesday || 0,
        daily_wednesday: data.daily_wednesday || '',
        hours_wednesday: data.hours_wednesday || 0,
        daily_thursday: data.daily_thursday || '',
        hours_thursday: data.hours_thursday || 0,
        daily_friday: data.daily_friday || '',
        hours_friday: data.hours_friday || 0,
        total_hours: calculateTotalHours(),
      };
      
      try {
        // Try to save to backend
        let updatedReport;
        if (reportData?.id) {
          // Update existing report
          updatedReport = await weeklyReportService.updateWeeklyReport(reportData.id, weeklyReportData);
        } else {
          // Create new report
          updatedReport = await weeklyReportService.createWeeklyReport(weeklyReportData);
        }
        
        // Update the report data with the backend response to get the main job ID
        if (updatedReport) {
          console.log('Weekly report saved, backend response:', updatedReport);
          console.log('Current operations before update:', reportData?.main_job?.operations);
          
          setReportData(prev => prev ? {
            ...prev,
            id: updatedReport.id,
            main_job: {
              id: updatedReport.main_job?.id,
              title: updatedReport.main_job?.title || data.main_job_title || 'Main Job',
              operations: prev.main_job?.operations || [] // Keep existing operations
            }
          } : prev);
          
          // Refresh operations from backend to ensure they're properly loaded
          if (updatedReport.main_job?.id) {
            try {
              console.log('Refreshing operations for main job ID:', updatedReport.main_job.id);
              const refreshedOperations = await mainJobService.getOperations(updatedReport.main_job.id);
              console.log('Refreshed operations from backend:', refreshedOperations);
              
              setReportData(prev => prev ? {
                ...prev,
                main_job: {
                  ...prev.main_job,
                  operations: refreshedOperations
                }
              } : prev);
            } catch (error) {
              console.warn('Failed to refresh operations after weekly report save:', error);
            }
          }
        }
        
        showSuccess('Report saved successfully!');
      } catch (backendError) {
        console.warn('Backend save failed, saving locally:', backendError);
        // Save locally when backend is unavailable
        const updatedReportData: WeeklyReportData = {
          id: reportData?.id || Date.now(), // Use timestamp as local ID
          week_number: reportData?.week_number || weekNum,
          start_date: reportData?.start_date || weekDates.startDateStr,
          end_date: reportData?.end_date || weekDates.endDateStr,
          total_hours: calculateTotalHours(),
          daily_reports: reportData?.daily_reports || [],
          main_job: reportData?.main_job || {
            title: '',
            operations: []
          }
        };
        setReportData(updatedReportData);
        showInfo('Report saved locally ');
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save report:', error);
      showError('Failed to save report. Please try again.');
    }
  };

  const handleAddDailyReport = async (data: any) => {
    try {
      if (!weekNumber) {
        showError('Week number not found');
        return;
      }
      const weekNum = parseInt(weekNumber);

      // Check for duplicate
      if (reportData?.daily_reports.some(r => r.date === data.date)) {
        showWarning('A daily report for this date already exists.');
        return;
      }

      const payload: CreateDailyReportData = { 
        ...data, 
        week_number: weekNum,
        date: data.date,
        description: data.description,
        hours_spent: parseFloat(data.hours_spent)
      };
      
      // POST to backend
      await weeklyReportService.createDailyReport(payload);
      
      // Re-fetch daily reports for this week
      const weekDailyReports = await weeklyReportService.getDailyReportsByWeek(weekNum);
      
      // Update state with new daily reports
      setReportData(prev => prev ? { 
        ...prev, 
        daily_reports: weekDailyReports,
        total_hours: weekDailyReports.reduce((sum, report) => {
          const hours = typeof report.hours_spent === 'number' ? report.hours_spent : parseFloat(report.hours_spent) || 0;
          return sum + hours;
        }, 0)
      } : prev);
      
      setIsAddingDaily(false);
      showSuccess('Daily report added successfully!');
    } catch (error: any) {
      // Log and display backend error message
      const backendMsg = error?.response?.data?.message || error?.response?.data?.detail || JSON.stringify(error?.response?.data) || error.message;
      showError('Failed to add daily report: ' + backendMsg);
      console.error('Failed to add daily report:', error);
    }
  };



  // Initialize editing operations when entering edit mode
  const handleStartEditingOperations = () => {
    if (reportData?.main_job?.operations) {
      // Deep copy operations to local state
      setEditingOperations(JSON.parse(JSON.stringify(reportData.main_job.operations)));
    }
    setIsEditingMainJob(true);
  };

  // Add a new empty operation row
  const handleAddOperationRow = () => {
    const newOperation: MainJobOperation = {
      id: Date.now(), // Temporary ID
      step_number: editingOperations.length + 1,
      operation_description: '',
      tools_used: '',
    };
    setEditingOperations([...editingOperations, newOperation]);
  };

  // Remove an operation row
  const handleRemoveOperationRow = (index: number) => {
    const updated = editingOperations.filter((_, i) => i !== index);
    // Renumber step numbers
    const renumbered = updated.map((op, i) => ({ ...op, step_number: i + 1 }));
    setEditingOperations(renumbered);
  };

  // Update operation field
  const handleUpdateOperation = (index: number, field: 'operation_description' | 'tools_used', value: string) => {
    const updated = [...editingOperations];
    updated[index][field] = value;
    setEditingOperations(updated);
  };

  // New handlers for main job operations editing
  const handleSaveMainJobOperations = async () => {
    try {
      if (!reportData?.main_job?.id) {
        showWarning('Main job not found. Please save the weekly report first.');
        return;
      }

      const mainJobId = reportData.main_job.id!;
      
      // Filter out empty operations (no description or tools)
      const validOperations = editingOperations.filter(
        op => op.operation_description.trim() !== '' || op.tools_used.trim() !== ''
      );

      if (validOperations.length === 0) {
        showWarning('Please add at least one operation with content.');
        return;
      }

      const updatedOperations: MainJobOperation[] = [];
      
      // First, delete all existing operations from backend to avoid conflicts
      try {
        for (const existingOp of reportData.main_job.operations) {
          await mainJobService.deleteOperation(mainJobId, existingOp.id);
        }
      } catch (error) {
        console.error('Failed to delete existing operations:', error);
        // Continue anyway - some operations might not exist
      }
      
      // Now create all operations with fresh step numbers
      for (let i = 0; i < validOperations.length; i++) {
        const operation = validOperations[i];
        const created = await mainJobService.createOperation(mainJobId, {
          main_job: mainJobId,
          step_number: i + 1, // Use sequential step numbers
          operation_description: operation.operation_description,
          tools_used: operation.tools_used,
        });
        updatedOperations.push(created);
      }

      // Update state with real database IDs
      setReportData(prev => prev ? {
        ...prev,
        main_job: {
          ...prev.main_job,
          operations: updatedOperations
        }
      } : prev);

      setEditingOperations([]);
      setIsEditingMainJob(false);
      showSuccess('Main job operations saved successfully!');
    } catch (error) {
      console.error('Failed to save main job operations:', error);
      showError('Failed to save main job operations: ' + (error as any)?.message || 'Unknown error');
    }
  };

  // Cancel editing operations
  const handleCancelEditingOperations = () => {
    setEditingOperations([]);
    setIsEditingMainJob(false);
  };

  const handleSaveMainJobTitle = async () => {
    try {
      if (!reportData?.main_job?.id) {
        showWarning('Main job not found. Please save the weekly report first.');
        return;
      }

      const formData = getValues();
      const newTitle = formData.main_job_title || reportData.main_job.title;

      // Update main job title in backend
      const mainJobId = reportData.main_job.id!;
      await mainJobService.updateMainJob(mainJobId, {
        title: newTitle
      });

      // Update state
      setReportData(prev => prev ? {
        ...prev,
        main_job: {
          ...prev.main_job,
          title: newTitle
        }
      } : prev);

      // Refresh operations to ensure they're properly loaded
      try {
        const refreshedOperations = await mainJobService.getOperations(mainJobId);
        setReportData(prev => prev ? {
          ...prev,
          main_job: {
            ...prev.main_job,
            operations: refreshedOperations
          }
        } : prev);
      } catch (error) {
        console.warn('Failed to refresh operations after title update:', error);
      }

      setIsEditingMainJobTitle(false);
      showSuccess('Main job title saved successfully!');
    } catch (error) {
      console.error('Failed to save main job title:', error);
      showError('Failed to save main job title: ' + (error as any)?.message || 'Unknown error');
    }
  };

  const isTimeoutError = (error: any) =>
    error?.code === 'ECONNABORTED' || String(error?.message || '').includes('timeout');

  const downloadReportBlob = (weekNum: number, format: 'pdf' | 'docx'): Promise<Blob> =>
    format === 'pdf'
      ? weeklyReportService.downloadWeeklyReportPDF(weekNum)
      : weeklyReportService.downloadWeeklyReportDOCX(weekNum);

// Export endpoints answer with responseType 'blob', so error payloads arrive as
  // blobs too. Pull the message out so the user sees why instead of a broken file.
  const readBlobErrorPayload = async (error: any): Promise<any | null> => {
    const data = error?.response?.data;
    if (!(data instanceof Blob)) return null;
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  };

  const readBlobErrorMessage = async (error: any): Promise<string | null> => {
    const parsed = await readBlobErrorPayload(error);
    return parsed?.message || parsed?.detail || (parsed?.error ? String(parsed.error) : null);
  };

  // 200 tokens for AI-enhanced reports, 100 for normal reports.
  const exportTokenCost = reportData?.is_ai_enhanced ? 200 : 100;
  const availableTokens = userBalance?.available_tokens ?? 0;
  const canAffordExport = availableTokens >= exportTokenCost;

  const handleDownloadReport = async (format: 'pdf' | 'docx') => {
    // Guards against a second click firing a duplicate render job, which on a
    // shared worker makes an already slow generation look like a total hang.
    if (isExporting) return;

    try {
      if (!weekNumber) return;
      
      const weekNum = parseInt(weekNumber);
      
      // Check if there's actual data to download
      if (!reportData || reportData.daily_reports.length === 0) {
        showWarning('⚠️ No data available to download. Please add daily reports and complete the weekly report before downloading.');
        return;
      }
      
      // Check if the report has meaningful content
      const hasContent = reportData.daily_reports.some(report => 
        report.description && report.description.trim().length > 0
      );
      
      if (!hasContent) {
        showWarning('⚠️ The report contains no content. Please add descriptions to your daily reports before downloading.');
        return;
      }
      
      // Block the export up front when the user cannot afford it. The backend
      // enforces this too, but this avoids a wasted generation round-trip.
      if (!canAffordExport) {
        showWarning(
          `Not enough tokens to export this ${reportData.is_ai_enhanced ? 'enhanced' : 'normal'} report. ` +
          `It costs ${exportTokenCost} tokens and you have ${availableTokens}. ` +
          `Please top up your balance.`
        );
        return;
      }

      setIsExporting(true);

      let blob: Blob;
      try {
        blob = await downloadReportBlob(weekNum, format);
      } catch (primaryError: any) {
        console.error(`Week download endpoint failed for ${format}:`, primaryError);

        // An insufficient-token response must not trigger the fallback route:
        // that would charge again (or, worse, export without enough tokens).
        const primaryPayload = await readBlobErrorPayload(primaryError);
        if (primaryPayload?.requires_tokens) {
          showError(primaryPayload.message || 'Not enough tokens to export this report.');
          fetchUserBalance();
          return;
        }

        // The per-week route and the per-report route are separate backend views.
        // If the first one stalls, retry the other before giving up.
        if (reportData.id) {
          try {
            blob = format === 'pdf'
              ? await exportService.exportWeeklyPDF(reportData.id)
              : await exportService.exportWeeklyDOCX(reportData.id);
          } catch (fallbackError) {
            console.error(`Report download endpoint failed for ${format}:`, fallbackError);
            const serverMessage = await readBlobErrorMessage(primaryError) || await readBlobErrorMessage(fallbackError);
            if (serverMessage) {
              showError(serverMessage);
            } else if (isTimeoutError(primaryError) || isTimeoutError(fallbackError)) {
              showError(`${format.toUpperCase()} generation timed out. The server is busy, please try again in a moment.`);
            } else {
              showError(`Failed to generate the ${format.toUpperCase()} report. Please try again.`);
            }
            return;
          }
        } else {
          const serverMessage = await readBlobErrorMessage(primaryError);
          if (serverMessage) {
            showError(serverMessage);
          } else if (isTimeoutError(primaryError)) {
            showError(`${format.toUpperCase()} generation timed out. The server is busy, please try again in a moment.`);
          } else {
            showError(`Failed to generate the ${format.toUpperCase()} report. Please try again.`);
          }
          return;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `week-${weekNum}-report.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // The download endpoints already deducted the tokens server-side.
      showSuccess(`Report downloaded. ${exportTokenCost} tokens deducted.`);
      fetchUserBalance();
    } catch (error) {
      console.error(`Failed to download ${format.toUpperCase()}:`, error);
      showError(`Failed to download ${format.toUpperCase()} report. Please try again.`);
    } finally {
      setIsExporting(false);
    }
  };

  const refreshReportAfterRevert = async () => {
    if (!weekNumber) return;
    const weekNum = parseInt(weekNumber);
    try {
      const weeklyReport = await weeklyReportService.getWeeklyReportByWeek(weekNum);
      const weekDailyReports = await weeklyReportService.getDailyReportsByWeek(weekNum);

      setReportData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          id: weeklyReport?.id ?? prev.id,
          is_ai_enhanced: false,
          daily_reports: weekDailyReports || [],
          main_job: {
            ...(prev.main_job || {}),
            id: weeklyReport?.main_job?.id ?? prev.main_job?.id,
            title: weeklyReport?.main_job?.title || prev.main_job?.title || '',
            operations: prev.main_job?.operations || []
          }
        };
      });

      // Also refresh the operations when the main job changed during enhancement.
      if (weeklyReport?.main_job?.id) {
        try {
          const ops = await mainJobService.getOperations(weeklyReport.main_job.id);
          setReportData(prev => prev ? {
            ...prev,
            main_job: { ...(prev.main_job || {}), operations: ops }
          } : prev);
        } catch (e) {
          console.warn('Failed to refresh main job operations after revert:', e);
        }
      }

      const initialDailyHours: { [key: string]: number } = {};
      const initialDailyDescriptions: { [key: string]: string } = {};
      (weekDailyReports || []).forEach(report => {
        const reportDate = new Date(report.date);
        const dayName = reportDate.toLocaleDateString('en-US', { weekday: 'long' });
        if (dayName) {
          initialDailyHours[dayName] = report.hours_spent || 0;
          initialDailyDescriptions[dayName] = report.description || '';
        }
      });
      setDailyHours(initialDailyHours);
      setDailyDescriptions(initialDailyDescriptions);
    } catch (e) {
      console.error('Failed to refresh report after revert:', e);
    }
  };

  const handleRevertToOriginal = async () => {
    if (!weekNumber || isReverting) return;
    const weekNum = parseInt(weekNumber);
    const availableTokens = userBalance?.available_tokens ?? 0;

    if (userBalance && userBalance.available_tokens < REVERT_TOKEN_COST) {
      showWarning(
        `Not enough tokens to revert this report. It costs ${REVERT_TOKEN_COST} tokens and you have ${availableTokens}. ` +
        `Please top up your balance.`
      );
      return;
    }

    setIsReverting(true);
    try {
      const result = await weeklyReportService.revertWeeklyReport(weekNum);
      if (!result.success) {
        throw new Error(result.message || 'Revert failed');
      }
      showSuccess(
        `Report reverted to your original input. ${result.tokens_deducted ?? REVERT_TOKEN_COST} tokens deducted.`
      );
      setShowRevertModal(false);
      fetchUserBalance();
      await refreshReportAfterRevert();
    } catch (error: any) {
      console.error('Failed to revert report:', error);
      if (error?.requiresTokens) {
        showWarning(error.message || `You need ${REVERT_TOKEN_COST} tokens to revert this report.`);
        fetchUserBalance();
      } else {
        showError(error?.message || 'Could not revert the report. Please try again.');
      }
    } finally {
      setIsReverting(false);
    }
  };

  const getDayAbbreviation = (dayName: string) => {
    return dayName.substring(0, 3);
  };

  const getDailyReportForDay = (dayName: string) => {
    if (!reportData?.daily_reports) return undefined;
    return reportData.daily_reports.find(report => {
      const reportDate = new Date(report.date);
      const reportDayName = reportDate.toLocaleDateString('en-US', { weekday: 'long' });
      return reportDayName === dayName;
    });
  };

  const handleHoursChange = (dayName: string, hours: number) => {
    setDailyHours(prev => ({
      ...prev,
      [dayName]: hours
    }));
  };

  const handleDescriptionChange = (dayName: string, description: string) => {
    setDailyDescriptions(prev => ({
      ...prev,
      [dayName]: description
    }));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-3">
        <LoadingSpinner size="md" message="Loading weekly report..." />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-3">
        <AlertCircle className={`w-5 h-5 text-${theme}-500 mb-2`} />
        <p className="text-gray-600 text-xs mb-4">Weekly report not found</p>
        <button
          onClick={async () => {
            try {
              if (!weekNumber) return;
              const weekNum = parseInt(weekNumber);
              const weekDates = getWeekDates(weekNum, getTrainingYear());
              
              await weeklyReportService.createWeeklyReport({
                week_number: weekNum,
                start_date: weekDates.startDateStr,
                end_date: weekDates.endDateStr,
                summary: `Week ${weekNum} Report`,
                main_job_title: 'Main Job',
                main_job_description: 'Description of main job'
              });
              
              // Reload the page data
              window.location.reload();
            } catch (error) {
              console.error('Failed to create report:', error);
              showError('Failed to create report. Please try again.');
            }
          }}
          className={`btn-primary flex items-center gap-2 px-4 py-2 text-sm`}
        >
          <PlusCircle className="w-4 h-4" />
          Create Week {weekNumber} Report
        </button>
      </div>
    );
  }

  return (
    <div className="p-2 lg:p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-4 lg:mb-6">
        <div className="flex items-center gap-2 lg:gap-4 mb-3 lg:mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className={`p-1.5 lg:p-2 rounded-lg bg-${theme}-100 text-${theme}-600 hover:bg-${theme}-200`}
          >
            <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
          </button>
          <div>
            <h1 className={`text-lg lg:text-2xl xl:text-3xl font-bold text-${theme}-600`}>
              Week {reportData.week_number} Report
            </h1>
            <p className="text-gray-600 text-xs lg:text-sm">
              {formatDate(reportData.start_date)} - {formatDate(reportData.end_date)}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 lg:gap-4">
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu((v) => !v)}
              disabled={isExporting}
              className={`btn-secondary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-haspopup="menu"
              aria-expanded={showExportMenu}
            >
              <FileDown className="w-3 h-3 lg:w-4 lg:h-4" />
              {isExporting ? 'Generating...' : 'Export'}
            </button>
            {showExportMenu && (
              <div
                role="menu"
                className="absolute z-40 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden"
              >
                {!canAffordExport && (
                  <div className="px-3 py-2 text-xs text-red-600 bg-red-50 space-y-1">
                    <p>
                      Not enough tokens. This export costs {exportTokenCost} tokens,
                      you have {availableTokens}.
                    </p>
                    <p>
                      <button
                        type="button"
                        onClick={() => navigate('/billing')}
                        className="inline-flex items-center gap-1 font-semibold text-red-700 underline underline-offset-2 hover:text-red-900"
                      >
                        Get more tokens
                      </button>{' '}
                      to unlock this export.
                    </p>
                  </div>
                )}
                <button
                  role="menuitem"
                  disabled={isExporting || !canAffordExport}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setShowExportMenu(false);
                    handleDownloadReport('pdf');
                  }}
                >
                  PDF
                  </button>
                <button
                  role="menuitem"
                  disabled={isExporting || !canAffordExport}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setShowExportMenu(false);
                    handleDownloadReport('docx');
                  }}
                >
                  DOCX
                </button>
              </div>
            )}
          </div>

          {/* Shortcut to the AI assistant for this week */}
          <div className="ml-auto flex items-center">
            {reportData?.is_ai_enhanced ? (
              <button
                onClick={() => setShowRevertModal(true)}
                title={`Week ${weekNumber} report is enhanced. Click to revert to your original input.`}
                className="inline-flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2 whitespace-nowrap rounded-lg bg-amber-100 text-amber-700 ring-1 ring-amber-300 hover:bg-amber-200 hover:text-amber-800"
              >
                <CheckCircle2 className="w-3 h-3 lg:w-4 lg:h-4" />
                Enhanced. Click to revert
              </button>
            ) : (
              <button
                onClick={() => navigate(`/enhancement?week=${weekNumber}`)}
                title={`Open the AI assistant for Week ${weekNumber}`}
                className="btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2 whitespace-nowrap"
              >
                <Sparkles className="w-3 h-3 lg:w-4 lg:h-4" />
                Enhance Report
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div
        className="space-y-4 lg:space-y-6"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
      >
        {/* Report Header */}
        <div className="card p-3 lg:p-4">
          <div className="text-center mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-2">
              {getCollegeName()}
            </h2>
            <div className="flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-8 text-xs lg:text-sm">
              <div className="flex items-center gap-1 lg:gap-2">
                <span className="font-medium">Weekly Report No:</span>
                <span className="font-bold">{reportData.week_number}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Horizontal Scroll Tabs */}
        <div className="card p-0 overflow-hidden">
          <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200">
            <button
              onClick={() => setActiveTab('daily')}
              className={`flex-shrink-0 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'daily'
                  ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Daily Activity
            </button>
            <button
              onClick={() => setActiveTab('mainjob')}
              className={`flex-shrink-0 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'mainjob'
                  ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Main Job
            </button>
            <button
              onClick={() => setActiveTab('diagram')}
              className={`flex-shrink-0 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'diagram'
                  ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Diagrams
            </button>
          </div>
        </div>

        {/* Daily Work Log */}
        {activeTab === 'daily' && (
        <div className="card p-3 lg:p-4">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-xl xl:text-2xl font-bold text-gray-900">Daily Work Log</h2>
            <div className="flex gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className={`btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                >
                  <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSubmit(handleSaveReport)}
                    className={`btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                  >
                    <Save className="w-3 h-3 lg:w-4 lg:h-4" />
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className={`btn-secondary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                  >
                    <X className="w-3 h-3 lg:w-4 lg:h-4" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-xs lg:text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-left font-semibold w-12">Day</th>
                  <th className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3 text-left font-semibold">Brief description of work performed</th>
                  <th className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-left font-semibold w-16">hr</th>
                </tr>
              </thead>
              <tbody>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((dayName) => {
                  const dailyReport = getDailyReportForDay(dayName);
                  // Ensure hours are properly converted to numbers
                  let currentHours = 0;
                  if (dailyHours[dayName] !== undefined) {
                    currentHours = typeof dailyHours[dayName] === 'number' ? dailyHours[dayName] : parseFloat(dailyHours[dayName]) || 0;
                  } else if (dailyReport?.hours_spent !== undefined) {
                    currentHours = typeof dailyReport.hours_spent === 'number' ? dailyReport.hours_spent : parseFloat(dailyReport.hours_spent) || 0;
                  }
                  const currentDescription = dailyDescriptions[dayName] ?? dailyReport?.description ?? '';
                  
                                      return (
                      <tr key={dayName} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 font-medium text-center">
                          {getDayAbbreviation(dayName)}
                        </td>
                                                <td className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3">
                          {isEditing ? (
                            <textarea
                              {...register(`daily_${dayName.toLowerCase()}`)}
                              defaultValue={currentDescription}
                              onChange={(e) => handleDescriptionChange(dayName, e.target.value)}
                              className="w-full p-1 lg:p-2 border border-gray-300 rounded text-xs resize-none"
                              placeholder="Describe work performed..."
                              rows={4}
                              style={{ minHeight: '80px' }}
                            />
                          ) : (
                            <p className="text-gray-900 text-xs lg:text-sm">{currentDescription || 'No work recorded'}</p>
                          )}
                        </td>
                                              <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-center">
                          {isEditing ? (
                            <input
                              type="number"
                              {...register(`hours_${dayName.toLowerCase()}`)}
                              defaultValue={currentHours > 0 ? currentHours : ''}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                handleHoursChange(dayName, value);
                              }}
                              onFocus={(e) => e.target.select()}
                              className="w-12 p-1 border border-gray-300 rounded text-xs text-center"
                              min="0"
                              max="24"
                              step="0.5"
                            />
                          ) : (
                            <span className="font-medium text-xs lg:text-sm">{currentHours}</span>
                          )}
                        </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-xs lg:text-sm text-center">Total</td>
                  <td className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3"></td>
                  <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-center text-xs lg:text-sm">
                    {isEditing ? calculateTotalHours() : calculateTotalHours()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Main Job Section */}
        {activeTab === 'mainjob' && (
        <div className="card p-3 lg:p-4">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-xl xl:text-2xl font-bold text-gray-900">Main Job & Operations</h2>
            <div className="flex gap-2">
              {!isEditingMainJob ? (
                <button
                  onClick={handleStartEditingOperations}
                  className={`btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                >
                  <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                  Edit Operations
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveMainJobOperations}
                    className={`btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                  >
                    <Save className="w-3 h-3 lg:w-4 lg:h-4" />
                    Save Operations
                  </button>
                  <button
                    onClick={handleCancelEditingOperations}
                    className={`btn-secondary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                  >
                    <X className="w-3 h-3 lg:w-4 lg:h-4" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Main Job Title */}
          <div className="mb-4 lg:mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs lg:text-sm font-medium">Main Job Title</label>
              {!isEditingMainJobTitle ? (
                <button
                  onClick={() => setIsEditingMainJobTitle(true)}
                  className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
                >
                  <Edit className="w-3 h-3" />
                  Edit Title
                </button>
              ) : (
                <div className="flex gap-1">
                  <button
                    onClick={handleSaveMainJobTitle}
                    className="text-green-600 hover:text-green-800 text-xs flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditingMainJobTitle(false)}
                    className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
            {isEditingMainJobTitle ? (
              <input
                {...register('main_job_title')}
                defaultValue={reportData.main_job.title}
                className="input-field text-xs lg:text-sm"
                placeholder="Enter main job title..."
              />
            ) : (
              <p className="text-gray-900 text-sm lg:text-lg font-medium">{reportData.main_job.title || 'No main job defined'}</p>
            )}
          </div>

          {/* Operations Table */}
          {/* To adjust column widths, modify the w-* classes in the th elements above */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-xs lg:text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-left font-semibold w-8">#</th>
                  <th className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3 text-left font-semibold">Operation</th>
                  <th className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3 text-left font-semibold">Tools, Machinery, Equipment</th>
                  {isEditingMainJob && (
                    <th className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-left font-semibold w-16">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(isEditingMainJob ? editingOperations : reportData.main_job.operations).length > 0 ? (
                  (isEditingMainJob ? editingOperations : reportData.main_job.operations).map((operation, index) => (
                    <tr key={operation.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 font-medium text-center">
                        {operation.step_number}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3">
                        {isEditingMainJob ? (
                          <textarea
                            value={operation.operation_description}
                            onChange={(e) => handleUpdateOperation(index, 'operation_description', e.target.value)}
                            className="input-field input-field-sm textarea"
                            rows={5}
                            style={{ minHeight: '100px' }}
                            placeholder="Describe the operation..."
                          />
                        ) : (
                          <p className="text-gray-900 text-xs lg:text-sm">{operation.operation_description}</p>
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 lg:px-4 lg:py-3">
                        {isEditingMainJob ? (
                          <textarea
                            value={operation.tools_used}
                            onChange={(e) => handleUpdateOperation(index, 'tools_used', e.target.value)}
                            className="w-full p-1 lg:p-2 border border-gray-300 rounded text-xs resize-none"
                            rows={3}
                            style={{ minHeight: '60px' }}
                            placeholder="List tools, machinery, equipment..."
                          />
                        ) : (
                          <p className="text-gray-900 text-xs lg:text-sm">{operation.tools_used}</p>
                        )}
                      </td>
                      {isEditingMainJob && (
                        <td className="border border-gray-300 px-1 py-2 lg:px-2 lg:py-3 text-center">
                          <button
                            onClick={() => handleRemoveOperationRow(index)}
                            className="text-red-500 hover:text-red-700 text-xs"
                            title="Remove operation"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={isEditingMainJob ? 4 : 3} className="border border-gray-300 px-2 py-4 lg:px-4 lg:py-8 text-center text-gray-500 text-xs lg:text-sm">
                      No operations defined yet
                    </td>
                  </tr>
                )}
                
                {/* Add new operation row button when editing main job */}
                {isEditingMainJob && (
                  <tr className="hover:bg-gray-50">
                    <td colSpan={4} className="border border-gray-300 px-2 py-3 lg:px-4 lg:py-4 text-center">
                      <button
                        onClick={handleAddOperationRow}
                        className="btn-secondary flex items-center gap-1 mx-auto text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2"
                      >
                        <PlusCircle className="w-3 h-3 lg:w-4 lg:h-4" />
                        Add Operation
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Diagram Section */}
        {activeTab === 'diagram' && (
        <div className="card p-3 lg:p-4 scroll-mt-24">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-xl xl:text-2xl font-bold text-gray-900">Diagram Page</h2>
            <div className="flex gap-2">
              {!isEditingDiagram ? (
                <button
                  onClick={() => setIsEditingDiagram(true)}
                  className={`btn-primary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                >
                  <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                  Edit Diagram
                </button>
              ) : (
                <button
                  onClick={() => setIsEditingDiagram(false)}
                  className={`btn-secondary flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2`}
                >
                  <X className="w-3 h-3 lg:w-4 lg:h-4" />
                  Close Editor
                </button>
              )}
            </div>
          </div>

          {reportData?.id && (
            <DiagramEditor 
              weeklyReportId={reportData.id} 
              isEditing={isEditingDiagram}
            />
          )}
        </div>
        )}
      </div>

      {/* Add Daily Report Modal */}
      {isAddingDaily && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 lg:p-6 w-full max-w-md">
            <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Add Daily Report</h3>
            <form onSubmit={handleSubmit(handleAddDailyReport)} className="space-y-3 lg:space-y-4">
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1 lg:mb-2">Date</label>
                <input
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  className="input-field text-xs lg:text-sm"
                />
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date.message?.toString()}</p>}
              </div>
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1 lg:mb-2">Description</label>
                <textarea
                  {...register('description', { required: 'Description is required' })}
                  className="input-field text-xs lg:text-sm"
                  rows={3}
                  placeholder="Describe the work performed..."
                />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message?.toString()}</p>}
              </div>
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1 lg:mb-2">Hours Spent</label>
                <input
                  type="number"
                  {...register('hours_spent', { required: 'Hours is required', min: 0, max: 24 })}
                  className="input-field text-xs lg:text-sm"
                  min="0"
                  max="24"
                  step="0.5"
                  onFocus={(e) => e.target.select()}
                />
                {errors.hours_spent && <p className="text-red-500 text-xs mt-1">{errors.hours_spent.message?.toString()}</p>}
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary flex-1 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2">Add Report</button>
                <button
                  type="button"
                  onClick={() => setIsAddingDaily(false)}
                  className="btn-secondary flex-1 text-xs lg:text-sm px-3 py-1.5 lg:px-4 lg:py-2"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRevertModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 sm:p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Revert to original report?</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                  You're about to remove the AI-generated content from this report and restore your original
                  input. This action costs{' '}
                  <span className="font-semibold text-gray-900">{REVERT_TOKEN_COST} tokens</span> and{' '}
                  <span className="font-semibold text-gray-900">cannot be undone</span>.
                </p>
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 space-y-1">
                  <p>• All AI enhancements will be removed</p>
                  <p>• The report will return to your original content</p>
                  <p>• {REVERT_TOKEN_COST} tokens will be deducted from your balance</p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setShowRevertModal(false)}
                disabled={isReverting}
                className="flex-1 px-4 h-10 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevertToOriginal}
                disabled={isReverting || (userBalance ? userBalance.available_tokens < REVERT_TOKEN_COST : false)}
                className="flex-1 px-4 h-10 rounded-xl bg-amber-600 hover:bg-amber-700 text-sm font-semibold text-white shadow-sm transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {isReverting ? (
                  <LoadingSpinner size="sm" inline color="white" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {isReverting ? 'Reverting…' : `Revert to original · ${REVERT_TOKEN_COST} tokens`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}; 